import { Controller } from "@hotwired/stimulus"
import {
  wordCount,
  sentenceStats,
  countJargon,
  firstSentence,
  lineCount,
} from "../lib/drill_helpers"

export default class extends Controller {
  static targets = [
    "draft",
    "wordCount",
    "targetMet",
    "firstSentence",
    "firstSentencePreview",
    "sentenceStats",
    "jargonHits",
    "jargonChip",
    "filledCount",
    "qChip",
  ]

  static values = {
    kind: String,
    targetWords: { type: Object, default: {} },
    firstSentenceWords: Number,
    targetAvgSentenceWords: Number,
    targetCount: Number,
    ratio: Number,
    source: { type: String, default: "" },
    sourceWordCount: Number,
    jargon: { type: Array, default: [] },
  }

  connect() {
    this.recompute()
  }

  update() {
    this.recompute()
  }

  // Listens for source-picker:change events bubbling on the window.
  // Wired via data-action="source-picker:change@window->drill#sourceChange".
  sourceChange(event) {
    const text = (event.detail && event.detail.text) || ""
    this.sourceValue = text
    this.sourceWordCountValue = wordCount(text)
    // Auto-populate draft (the "use example feels first-class" pattern):
    // only when the primary draft is empty AND we have non-empty source.
    if (this.hasDraftTarget && this.draftTarget.value.trim().length === 0 && text.trim().length > 0) {
      this.draftTarget.value = text
    }
    this.recompute()
  }

  recompute() {
    switch (this.kindValue) {
      case "cut_half":
        return this.recomputeCutHalf()
      case "lead_with_question":
        return this.recomputeLeadWithQuestion()
      case "short_sentences":
        return this.recomputeShortSentences()
      case "de_jargon":
        return this.recomputeDeJargon()
      case "elevator":
        return this.recomputeElevator()
      case "list_three":
        return this.recomputeListThree()
      case "probing_questions":
        return this.recomputeProbing()
      default:
        return
    }
  }

  recomputeCutHalf() {
    const text = this.primaryDraftText
    const count = wordCount(text)
    const target = this.cutHalfTargetWords
    const onTarget = target > 0 && count > 0 && count <= target
    this.writeText("wordCount", count)
    this.toggleOnTarget(onTarget)
  }

  recomputeLeadWithQuestion() {
    const fs = firstSentence(this.primaryDraftText)
    const fsCount = wordCount(fs)
    const target = this.firstSentenceWordsValue || 0
    const onTarget = target > 0 && fsCount > 0 && fsCount <= target
    this.writeText("firstSentence", fsCount)
    if (this.hasFirstSentencePreviewTarget) {
      this.firstSentencePreviewTarget.textContent = fs || "—"
      // The on-target class lives on the wrapper box, not the value span;
      // CSS uses `.first-sentence-preview--on-target .first-sentence-preview__value`.
      const wrapper = this.firstSentencePreviewTarget.closest(".first-sentence-preview")
      if (wrapper) wrapper.classList.toggle("first-sentence-preview--on-target", onTarget)
    }
    this.toggleOnTarget(onTarget)
  }

  recomputeShortSentences() {
    const stats = sentenceStats(this.primaryDraftText)
    const target = this.targetAvgSentenceWordsValue || 0
    const onTarget = target > 0 && stats.sentences > 0 && stats.avg <= target
    this.writeText("sentenceStats", `${stats.sentences} sent · ${stats.avg}w avg`)
    this.toggleOnTarget(onTarget)
  }

  recomputeDeJargon() {
    const draft = this.primaryDraftText
    const draftLower = draft.toLowerCase()
    const sourceJargon = this.jargonInSource()
    // Hits = how many sourceJargon words still appear in draft.
    const remaining = sourceJargon.filter((j) => draftLower.includes(j.toLowerCase()))
    const onTarget = draft.trim().length > 0 && remaining.length === 0
    this.writeText("jargonHits", remaining.length)
    // Walk chip targets — each chip carries its word in data-jargon-word.
    // Toggle .jargon-chip--removed when the word is no longer in the draft.
    if (this.hasJargonChipTarget) {
      this.jargonChipTargets.forEach((chip) => {
        const word = (chip.dataset.jargonWord || "").toLowerCase()
        const stillThere = word.length > 0 && draftLower.includes(word)
        chip.classList.toggle("jargon-chip--removed", !stillThere)
      })
    }
    this.toggleOnTarget(onTarget)
  }

  recomputeElevator() {
    const count = wordCount(this.primaryDraftText)
    const min = this.targetWordsValue.min || 0
    const max = this.targetWordsValue.max || 0
    const inBand = min > 0 && max > 0 && count >= min && count <= max
    const hits = this.jargonValue.length ? countJargon(this.primaryDraftText, this.jargonValue) : []
    this.writeText("wordCount", count)
    if (this.hasJargonHitsTarget) this.writeText("jargonHits", hits.length)
    this.toggleOnTarget(inBand && hits.length === 0)
  }

  recomputeListThree() {
    const count = lineCount(this.primaryDraftText)
    const target = this.targetCountValue || 0
    const onTarget = target > 0 && count >= target
    this.writeText("wordCount", count)
    this.toggleOnTarget(onTarget)
  }

  recomputeProbing() {
    const filled = this.draftTargets.filter((t) => t.value.trim().length > 0).length
    const target = this.targetCountValue || this.draftTargets.length
    const onTarget = filled >= target
    this.writeText("filledCount", `${filled} / ${target}`)
    if (this.hasQChipTarget) {
      this.qChipTargets.forEach((chip, i) => {
        const d = this.draftTargets[i]
        chip.classList.toggle("q-chip--filled", !!(d && d.value.trim().length > 0))
      })
    }
    this.toggleOnTarget(onTarget)
  }

  get primaryDraftText() {
    return this.hasDraftTarget ? this.draftTarget.value : ""
  }

  get cutHalfTargetWords() {
    if (this.targetWordsValue && this.targetWordsValue.max) return this.targetWordsValue.max
    if (this.sourceWordCountValue && this.ratioValue) {
      return Math.round(this.sourceWordCountValue * this.ratioValue)
    }
    return 0
  }

  // For de-jargon: which jargon words from the full list actually appear in
  // the current source text? Used so chips only reflect words a learner
  // would actually need to remove.
  jargonInSource() {
    const list = this.jargonValue
    if (!list.length) return []
    const sourceLower = (this.sourceValue || "").toLowerCase()
    if (!sourceLower) return list
    return list.filter((j) => sourceLower.includes(j.toLowerCase()))
  }

  writeText(name, value) {
    const key = `has${name.charAt(0).toUpperCase()}${name.slice(1)}Target`
    if (!this[key]) return
    this[`${name}Target`].textContent = value
  }

  toggleOnTarget(on) {
    this.element.classList.toggle("drill--on-target", !!on)
    if (this.hasTargetMetTarget) {
      this.targetMetTarget.hidden = !on
    }
  }
}
